import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { ApiService } from '../../core/api.service';
import { LogApiService } from './logs.api.service';

import { HighAlert, HighAlertDto, HighAlertPost, HighAlertPut, HighAlertStatus, HighAlertWindow } from '../../models/high-alert.model';
import { TaskFormValue } from '../../models/task-dialog.model';
import { ProgramsStore } from '../stores/programs.store';
import { UsersStore } from '../stores/users.store';
import { HighAlertTaskFormValue } from '../../components/dialogs/task-dialogs/task.dialog.model';
import { ApiResponse } from '../../models/api-response.model';
import { QueryParams } from '../../core/api.service';
import { map } from 'rxjs/operators';
import { toUtcIso, zonedWallClockToUtcIso } from '../../shared/utils/datetime';

@Injectable({ providedIn: 'root' })
export class HighAlertsService extends LogApiService<HighAlert, HighAlertPost, HighAlertDto> {
  constructor(
    private api: ApiService,
    private programsStore: ProgramsStore,
    private usersStore: UsersStore
  ) {
    super(api, '/high_alerts');
  }

  override list(page = 1, perPage = 100): Observable<HighAlert[]> {
    const params: QueryParams = { page, per_page: perPage };
    return this.api.get<ApiResponse<HighAlertDto[]>>('/high_alerts', params).pipe(
      map((r) => r.data),
      map((dtos) => dtos.map((dto) => this.mapEntity(dto)))
    );
  }

  protected override mapEntity(dto: HighAlertDto): HighAlert {
    const programName = this.programsStore.getNameById(dto.program_id) ?? dto.program_id ?? 'No Program found'; // fallback if not found
    const createdByUserName = this.usersStore.getNameById(dto.created_by) ?? dto.created_by ?? 'No User found'; // fallback if not found
    const start = new Date(dto.start_dttm);
    const end = new Date(dto.end_dttm);
    const now = new Date();

    const status = now > end ? HighAlertStatus.PAST : HighAlertStatus.CURRENT;

    return {
      id: String(dto.id),
      programName: programName,
      programId: dto.program_id,
      window: dto.window as HighAlertWindow,
      createdByName: createdByUserName,
      createdById: dto.created_by,
      createdDate: new Date(dto.created_dttm),
      updatedDate: dto.updated_dttm,
      timezone: dto.timezone,
      updatedBy: dto.updated_by,
      startDate: start,
      endDate: end,
      notes: dto.notes,
      status,
    };
  }

  public override createPayload(formValue: HighAlertTaskFormValue, userId: string): HighAlertPost {
    return {
      program_id: formValue.programId,
      notes: formValue.notes,
      start_dttm: zonedWallClockToUtcIso(formValue.startDate, formValue.timezone),
      end_dttm:   zonedWallClockToUtcIso(formValue.endDate, formValue.timezone),
      window: formValue.window as HighAlertWindow,
      timezone: formValue.timezone,
      created_by: userId,
      created_dttm: new Date().toISOString(),
    };
  }

  buildUpdatedPayload(log: HighAlert, userId: string): HighAlertPut {
    return {
      id: Number(log.id),
      program_id: log.programId,
      notes: log.notes,
      start_dttm: toUtcIso(log.startDate),
      end_dttm:   toUtcIso(log.endDate),
      timezone: log.timezone,
      window: log.window,
      created_by: log.createdById,
      created_dttm: new Date(log.createdDate).toISOString(),
      updated_by: userId,
      updated_dttm: new Date().toISOString(),
      status: log.status,
    };
  }
}
